import { useEffect, useState, useRef } from 'react'
import { useParams, Navigate, useNavigate } from 'react-router-dom'
import { Plus, Trash, Check, X, Download, Upload, FilePdf } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { LexicalRichTextEditor } from '@/components/ui/lexical-editor'
// removed dialog-based creation
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { CopyButton } from '@/components/ui/copy-button'
import { useApp } from '@/contexts/AppContext'
import { getOpenQuestionCountInThread, isQuestionOpen } from '@/contexts/AppContext'
import { useGlobalHotkeys } from '@/hooks/useGlobalHotkeys'
import { useDebounce } from '@/hooks/useDebounce'
import { ArrowLeftIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import type { QANode, Answer, Thread } from '@/types'
import { storage } from '@/lib/storage'
import { useDialog } from '@/contexts/DialogContext'
import { exportThreadToPDF, exportAllThreadsToPDF } from '@/lib/pdf-export'

interface EntityPageProps { readOnly?: boolean }

export function EntityPage({ readOnly = false }: EntityPageProps) {
  const { entityId } = useParams<{ entityId: string }>()
  const { state, dispatch, isReady } = useApp()
  const { t } = useTranslation()
  const dialog = useDialog()
  const navigate = useNavigate()
  const [isCreating, setIsCreating] = useState(false)
  const [newThreadTitle, setNewThreadTitle] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOrder, setSortOrder] = useState<'none' | 'asc' | 'desc'>('none')
  
  // Alternate mode state
  const [isAlternateMode, setIsAlternateMode] = useState(false)
  const [threadAlternateOverrides, setThreadAlternateOverrides] = useState<Record<string, boolean>>({})
  const compactPrefLoadedRef = useRef(false)

  // Load compact mode preferences (global + per-thread) on mount
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [globalCompact, overrides] = await Promise.all([
          storage.getPreference('ui.compact.global'),
          storage.getPreference('ui.compact.threadOverrides')
        ])
        if (!cancelled) {
          if (typeof globalCompact === 'boolean') setIsAlternateMode(globalCompact)
          if (overrides && typeof overrides === 'object') setThreadAlternateOverrides(overrides as Record<string, boolean>)
        }
      } finally {
        if (!cancelled) compactPrefLoadedRef.current = true
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Persist global compact preference
  useEffect(() => {
    if (!compactPrefLoadedRef.current) return
    storage.savePreference('ui.compact.global', isAlternateMode)
  }, [isAlternateMode])
  const importInputRef = useRef<HTMLInputElement>(null)
  
  const debouncedSearchQuery = useDebounce(searchQuery, 150)

  // Back navigation with history fallback
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate(readOnly ? '/archivio' : '/entities', { replace: true })
    }
  }

  // Global save/cancel handler registries for compact (alternate) mode editors
  const saveHandlersRef = useRef<Set<() => void>>(new Set())
  const cancelHandlersRef = useRef<Set<() => void>>(new Set())
  const notifyEditingChange = () => {
    const anyHandlers = (saveHandlersRef.current.size + cancelHandlersRef.current.size) > 0
    const evt = new CustomEvent('entity-compact-editing-change', { detail: { editing: anyHandlers } })
    window.dispatchEvent(evt)
  }

  const registerSaveHandler = (fn: () => void) => { saveHandlersRef.current.add(fn); notifyEditingChange() }
  const unregisterSaveHandler = (fn: () => void) => { saveHandlersRef.current.delete(fn); notifyEditingChange() }
  const registerCancelHandler = (fn: () => void) => { cancelHandlersRef.current.add(fn); notifyEditingChange() }
  const unregisterCancelHandler = (fn: () => void) => { cancelHandlersRef.current.delete(fn); notifyEditingChange() }

  useEffect(() => {
    if (readOnly) return
    const shortcutsEnabled = state.settings?.shortcutsEnabled ?? true
    if (!shortcutsEnabled) return

    const handler = (e: KeyboardEvent) => {
      // Only handle if there are registered handlers
      if (saveHandlersRef.current.size === 0 && cancelHandlersRef.current.size === 0) return
      
      const key = (e.key || '').toLowerCase()
      const isKeyS = key === 's' || e.code === 'KeyS'
      const isKeyD = key === 'd' || e.code === 'KeyD'
      const isSaveCombo = isKeyS && (e.ctrlKey || e.metaKey)
      const isCancelCombo = isKeyD && (e.ctrlKey || e.metaKey)
      if (!isSaveCombo && !isCancelCombo) return
      e.preventDefault()
      e.stopPropagation()
      // Execute all open handlers
      if (isSaveCombo) {
        saveHandlersRef.current.forEach(fn => { try { fn() } catch { /* noop */ } })
      } else if (isCancelCombo) {
        cancelHandlersRef.current.forEach(fn => { try { fn() } catch { /* noop */ } })
      }
    }
    window.addEventListener('keydown', handler, true)
    document.addEventListener('keydown', handler, true)
    return () => {
      window.removeEventListener('keydown', handler, true)
      document.removeEventListener('keydown', handler, true)
    }
  }, [readOnly, state.settings?.shortcutsEnabled])

  // Reflect compact mode in URL query so Sidebar can surface relevant shortcuts
  useEffect(() => {
    const anyCompact = isAlternateMode || Object.values(threadAlternateOverrides).some(Boolean)
    const url = new URL(window.location.href)
    if (anyCompact) {
      if (url.searchParams.get('compact') !== '1') {
        url.searchParams.set('compact', '1')
        window.history.replaceState({}, '', url.toString())
      }
    } else {
      if (url.searchParams.has('compact')) {
        url.searchParams.delete('compact')
        window.history.replaceState({}, '', url.toString())
      }
    }
    // Notify listeners (Sidebar) about compact mode change
    const evt = new CustomEvent('entity-compact-mode-change', { detail: { compact: anyCompact } })
    window.dispatchEvent(evt)
  }, [isAlternateMode, threadAlternateOverrides])

  // Hoisted function to create a thread; uses entityId directly so it can be called before entity lookup
  function handleCreateThread() {
    const title = newThreadTitle.trim()
    if (!title) return
    if (!entityId) return
    dispatch({
      type: 'CREATE_THREAD',
      payload: {
        entityId: entityId,
        title
      }
    })
    setNewThreadTitle('')
    setIsCreating(false)
  }

  // While creating a new thread, capture Ctrl+S (save) and Ctrl+D (cancel), even if focus is inside the input
  useEffect(() => {
    if (readOnly) return
    const shortcutsEnabled = state.settings?.shortcutsEnabled ?? true
    if (!shortcutsEnabled) return
    if (!isCreating) return

    const handler = (e: KeyboardEvent) => {
      const key = (e.key || '').toLowerCase()
      const isKeyS = key === 's' || e.code === 'KeyS'
      const isKeyD = key === 'd' || e.code === 'KeyD'
      const isSaveCombo = isKeyS && (e.ctrlKey || e.metaKey)
      const isCancelCombo = isKeyD && (e.ctrlKey || e.metaKey)
      if (!isSaveCombo && !isCancelCombo) return
      e.preventDefault()
      e.stopPropagation()
      if (isSaveCombo) {
        handleCreateThread()
      } else if (isCancelCombo) {
        setIsCreating(false)
        setNewThreadTitle('')
      }
    }
    window.addEventListener('keydown', handler, true)
    document.addEventListener('keydown', handler, true)
    return () => {
      window.removeEventListener('keydown', handler, true)
      document.removeEventListener('keydown', handler, true)
    }
  }, [isCreating, newThreadTitle, readOnly, state.settings?.shortcutsEnabled])

  // Broadcast create-mode status (Ctrl+S/D active while creating a thread)
  useEffect(() => {
    const evt = new CustomEvent('entity-create-mode-change', { detail: { creating: isCreating } })
    window.dispatchEvent(evt)
  }, [isCreating])

  // Define handlers before using in hooks to avoid TDZ issues
  const handleCreateThreadShortcut = () => {
    setIsCreating(true)
  }

  const handleImportClick = () => {
    importInputRef.current?.click()
  }

  const handleImportThreadShortcut = () => {
    handleImportClick()
  }

  const handleExportAllThreads = async () => {
    if (readOnly) return
    try {
      await exportAllThreadsToPDF({
        entityName: entity.name,
        threads: threads,
        qaNodes: state.qaNodes
      })
    } catch (error) {
      console.error('Export all threads failed:', error)
      await dialog.error({ message: t('exportError') || 'Failed to export all threads to PDF' })
    }
  }

  // Setup keyboard shortcuts (unconditional hook, gated by enabled)
  useGlobalHotkeys({
    shortcuts: [
      { key: 'a', modifiers: {}, description: t('shortcutCreateThread'), action: handleCreateThreadShortcut },
      { key: 'z', modifiers: {}, description: t('shortcutImportThread'), action: handleImportThreadShortcut }
    ],
    enabled: !readOnly && (state.settings?.shortcutsEnabled ?? true)
  })

  if (!entityId) {
    return <Navigate to={readOnly ? "/archivio" : "/entities"} replace />
  }

  // Defer not-found decisions until hydration completes to support deep links on fresh load
  const entity = state.entities[entityId]
  if (!isReady) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">{t('loading') || 'Loading...'}</div>
      </div>
    )
  }
  if (!entity) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="lg" className="h-10 w-10 p-0" aria-label={t('back')} onClick={handleBack}>
            <ArrowLeftIcon className="size-7" />
          </Button>
          <h1 className="text-lg font-semibold">{t('entityNotFound') || 'Entity not found'}</h1>
        </div>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">
            {t('entityMissingMessage') || 'The requested entity does not exist in the current data.'}
          </p>
        </Card>
      </div>
    )
  }

  const isArchived = entity.isArchived
  const threads = entity.threadIds.map(id => state.threads[id]).filter(Boolean)
  const anyCompact = isAlternateMode || Object.values(threadAlternateOverrides).some(Boolean)
  
  // Apply search + optional sort
  let filteredThreads = threads.filter(thread =>
    thread.title.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
  )
  if (sortOrder !== 'none') {
    filteredThreads = [...filteredThreads].sort((a, b) =>
      sortOrder === 'asc'
        ? a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
        : b.title.localeCompare(a.title, undefined, { sensitivity: 'base' })
    )
  }

  const handleThreadAlternateChange = (threadId: string, checked: boolean) => {
    if (readOnly) return
    const normalized = Boolean(checked)
    setThreadAlternateOverrides(prev => {
      const next = { ...prev }
      if (normalized === isAlternateMode) {
        delete next[threadId]
      } else {
        next[threadId] = normalized
      }
      // Persist after local state is computed
      if (compactPrefLoadedRef.current) {
        storage.savePreference('ui.compact.threadOverrides', next)
      }
      return next
    })
  }

  // handleCreateThread defined earlier (hoisted)

  // While creating a new thread, capture Ctrl+S (save) and Ctrl+D (cancel), even if focus is inside the input
  

  

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    try {
      const text = await file.text()
      const parsed = JSON.parse(text)

      const hasStructuredPayload = parsed && typeof parsed === 'object' && 'thread' in parsed
      const rawThread = hasStructuredPayload ? parsed.thread : parsed
      const rawQANodes = hasStructuredPayload ? parsed.qaNodes : parsed?.qaNodes

      if (!rawThread || typeof rawThread !== 'object' || !rawThread.title || !rawThread.rootQuestionId) {
        throw new Error('Invalid thread structure')
      }

      const normalizedQANodes = rawQANodes && typeof rawQANodes === 'object' ? rawQANodes : {}
      const { qaNodes: _ignored, ...threadPayload } = rawThread

      dispatch({
        type: 'IMPORT_THREAD',
        payload: {
          thread: { ...threadPayload },
          entityId: entity.id,
          qaNodes: { ...normalizedQANodes }
        }
      } as any)
      await dialog.alert({ message: t('importThreadSuccess') })
    } catch (error) {
      console.error('Import thread failed:', error)
      await dialog.error({ message: t('importThreadError') })
    } finally {
      event.target.value = ''
    }
  }


  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="lg" className="h-10 w-10 p-0" aria-label={t('back')} onClick={handleBack}>
          <ArrowLeftIcon className="size-7" />
        </Button>
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <span>{entity.name}</span>
          <CopyButton value={entity.name || ''} title={t('copy') || 'Copy'} />
          {isArchived && <span className="ml-2 text-sm text-muted-foreground">({t('archivio')})</span>}
        </h1>
        {!readOnly && (
          <>
            <Button size="sm" className="bg-success text-success-foreground hover:bg-success/90" onClick={() => setIsCreating(true)}>
              <Plus size={14} className="mr-1" />
              {t('newThread')}
            </Button>
            <Button size="sm" variant="outline" onClick={handleImportClick}>
              <Upload size={14} className="mr-1" />
              {t('importThread')}
            </Button>
            
            <Input
              type="text"
              placeholder={t('searchThreads')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-md"
            />
            <Button
              variant="outline"
              onClick={() => setSortOrder(prev => prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none')}
              title={`Sort: ${sortOrder}`}
            >
              {sortOrder === 'none' ? 'A↕' : sortOrder === 'asc' ? 'A→Z' : 'Z→A'}
            </Button>
            <Checkbox 
              id="alternate-mode"
              checked={isAlternateMode}
              onCheckedChange={(checked) => setIsAlternateMode(Boolean(checked))}
            />
            <Label htmlFor="alternate-mode" className="text-sm cursor-pointer whitespace-nowrap">
              {t('alternateMode')}
            </Label>
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              className="text-pdf-export hover:text-pdf-export-hover hover:bg-pdf-export/10"
              onClick={handleExportAllThreads}
              disabled={threads.length === 0}
              aria-label={t('exportAllThreads')}
            >
              <FilePdf size={24} className="mr-2" />
              {t('exportAllThreads')}
            </Button>
          </>
        )}
      </div>

      {/* Inline create thread row (below search) */}
      {!readOnly && isCreating && (
        <div className="mb-4">
          <Card className="p-4 border-success-border bg-success-soft">
            <div className="space-y-2" onClick={(e) => { e.preventDefault(); e.stopPropagation() }}>
              <Label htmlFor="new-thread">{t('title')}</Label>
              <div className="flex items-center gap-2">
                <CopyButton value={newThreadTitle || ''} title={t('copy') || 'Copy'} />
                <Input
                  id="new-thread"
                  value={newThreadTitle}
                  onChange={(e) => setNewThreadTitle(e.target.value)}
                  placeholder={t('threadTitle')}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) { e.preventDefault(); handleCreateThread() }
                    else if (e.key === 'Escape') { e.preventDefault(); setIsCreating(false); setNewThreadTitle('') }
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button className="bg-success text-success-foreground hover:bg-success/90" onClick={handleCreateThread}>{t('create')}</Button>
                <Button className="bg-error text-error-foreground hover:bg-error/90" onClick={() => { setIsCreating(false); setNewThreadTitle('') }}>{t('cancel')}</Button>
              </div>
              {/* Shortcut hints moved to Sidebar only */}
            </div>
          </Card>
        </div>
      )}

      {/* Threads List */}
      {
        filteredThreads.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>{threads.length === 0 ? t('noThreadsYet') : 'No threads match your search.'}</p>
        </div>
      ) : (
        <div className={cn("grid", anyCompact ? "gap-2" : "gap-3")}>
          {filteredThreads.map((thread) => {
            const openQuestionCount = getOpenQuestionCountInThread(thread.rootQuestionId, state.qaNodes)
            const updatedDate = new Date(thread.updatedAt).toLocaleDateString()

            // Find the last node inside this thread using a DFS similar to ThreadPage
            const getLastNode = (nodeId: string): QANode | undefined => {
              const node = state.qaNodes[nodeId] as QANode | undefined
              if (!node) return undefined
              let last: QANode = node
              if (node.type === 'question' && node.answerId) {
                const child = getLastNode(node.answerId)
                if (child) last = child
              } else if (node.type === 'answer' && node.followUpQuestionIds.length > 0) {
                for (const qid of node.followUpQuestionIds) {
                  const child = getLastNode(qid)
                  if (child) last = child
                }
              }
              return last
            }

            const lastNode = getLastNode(thread.rootQuestionId)
            const bgClass = (() => {
              if (!lastNode) return 'bg-white'
              if (lastNode.type === 'answer') {
                return (lastNode as Answer).status === 'conclusive' ? 'bg-success-soft' : 'bg-error-soft'
              }
              // question
              const open = isQuestionOpen(lastNode.id, state.qaNodes)
              return open ? 'bg-warning-soft' : 'bg-card'
            })()
            const isThreadAlternate = (threadAlternateOverrides[thread.id] ?? isAlternateMode)
            const threadPath = readOnly
              ? `/archivio/${entityId}/threads/${thread.id}`
              : `/entities/${entityId}/threads/${thread.id}`
            const navigateToThread = () => navigate(threadPath)

            return isThreadAlternate ? (
              <CompactThreadView
                key={thread.id}
                thread={thread}
                bgClass={bgClass}
                openQuestionCount={openQuestionCount}
                updatedDate={updatedDate}
                readOnly={readOnly}
                qaNodes={state.qaNodes}
                dispatch={dispatch}
                onNavigate={navigateToThread}
                isAlternate={isThreadAlternate}
                onToggleAlternate={(value) => handleThreadAlternateChange(thread.id, value)}
                registerSaveHandler={registerSaveHandler}
                unregisterSaveHandler={unregisterSaveHandler}
                registerCancelHandler={registerCancelHandler}
                unregisterCancelHandler={unregisterCancelHandler}
              />
            ) : (
              <ThreadListItem
                key={thread.id}
                thread={thread}
                bgClass={bgClass}
                openQuestionCount={openQuestionCount}
                updatedDate={updatedDate}
                readOnly={readOnly}
                onNavigate={navigateToThread}
                isAlternate={isThreadAlternate}
                onToggleAlternate={(value) => handleThreadAlternateChange(thread.id, value)}
                registerSaveHandler={registerSaveHandler}
                unregisterSaveHandler={unregisterSaveHandler}
                registerCancelHandler={registerCancelHandler}
                unregisterCancelHandler={unregisterCancelHandler}
              />
            )
          })}
        </div>
      )}
      
      <input
        ref={importInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={handleImportFile}
      />
    </div>
  )
}

interface ThreadListItemProps {
  thread: Thread
  bgClass: string
  openQuestionCount: number
  updatedDate: string
  readOnly: boolean
  onNavigate: () => void
  isAlternate: boolean
  onToggleAlternate: (value: boolean) => void
  registerSaveHandler: (fn: () => void) => void
  unregisterSaveHandler: (fn: () => void) => void
  registerCancelHandler: (fn: () => void) => void
  unregisterCancelHandler: (fn: () => void) => void
}

function ThreadListItem({ thread, bgClass, openQuestionCount, updatedDate, readOnly, onNavigate, isAlternate, onToggleAlternate, registerSaveHandler, unregisterSaveHandler, registerCancelHandler, unregisterCancelHandler }: ThreadListItemProps) {
  const { t } = useTranslation()
  const { state, dispatch } = useApp()
  const dialog = useDialog()
  const verboseMode = state.settings?.verboseMode ?? true
  const [isEditing, setIsEditing] = useState(false)
  const [title, setTitle] = useState(thread.title)
  const threadTitleTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Move cursor to end when editing thread title
  useEffect(() => {
    if (isEditing && threadTitleTextareaRef.current) {
      const textarea = threadTitleTextareaRef.current
      const length = textarea.value.length
      textarea.setSelectionRange(length, length)
    }
  }, [isEditing])

  // Register global save/cancel handlers while editing thread title
  useEffect(() => {
    if (readOnly || !isEditing) return
    const saveCb = () => save()
    const cancelCb = () => cancel()
    registerSaveHandler(saveCb)
    registerCancelHandler(cancelCb)
    return () => {
      unregisterSaveHandler(saveCb)
      unregisterCancelHandler(cancelCb)
    }
  }, [isEditing, title, thread.title, readOnly])

  const onTitleClick: React.MouseEventHandler = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!readOnly) {
      setIsEditing(true)
    }
  }

  const save = () => {
    const next = title.trim()
    if (!next) return
    if (next !== thread.title) {
      dispatch({ type: 'UPDATE_THREAD', payload: { id: thread.id, title: next } } as any)
    }
    setIsEditing(false)
  }

  const cancel = () => {
    setTitle(thread.title)
    setIsEditing(false)
  }

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
      e.preventDefault()
      save()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      // Collect all QA nodes for this thread
      const relatedQANodes: Record<string, any> = {}
      
      const collectQANodes = (nodeId: string) => {
        const node = state.qaNodes[nodeId]
        if (node) {
          relatedQANodes[nodeId] = node
          if (node.type === 'answer' && node.followUpQuestionIds) {
            node.followUpQuestionIds.forEach(collectQANodes)
          }
          if (node.type === 'question' && node.answerId) {
            collectQANodes(node.answerId)
          }
        }
      }
      
      if (thread.rootQuestionId) {
        collectQANodes(thread.rootQuestionId)
      }
      
      const threadData = {
        ...thread,
        qaNodes: relatedQANodes
      }
      
      const blob = new Blob([JSON.stringify(threadData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      const timestamp = new Date().toISOString().replace(/[:]/g, '-').split('.')[0]
      anchor.href = url
      anchor.download = `thread-${thread.title.replace(/[^a-zA-Z0-9]/g, '_')}-${timestamp}.json`
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
    }
  }

  const handleExportPDF = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      const entity = state.entities[Object.keys(state.entities).find(id => 
        state.entities[id].threadIds.includes(thread.id)
      ) || '']
      
      await exportThreadToPDF({
        thread,
        qaNodes: state.qaNodes,
        entityName: entity?.name || 'Unknown'
      })
    } catch (error) {
      console.error('PDF export failed:', error)
      await dialog.error({ message: t('exportError') || 'Failed to export PDF' })
    }
  }

  const alternateLabel = t('alternateMode')

  const handleNavigate = () => {
    if (isEditing) return
    onNavigate()
  }

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isEditing) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onNavigate()
    }
  }

  return (
    <Card
      className={cn(
  "p-4 transition-colors border border-border",
        bgClass,
        isEditing ? "cursor-default" : "cursor-pointer"
      )}
      role="button"
      tabIndex={0}
      onClick={handleNavigate}
      onKeyDown={handleCardKeyDown}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1">
          {!readOnly && (
            <div className="flex gap-1">
              <Button
                variant="destructive"
                size="sm"
                aria-label="Delete thread"
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const confirmed = await dialog.confirm({
                    message: t('confirmDeleteThread', { title: thread.title }),
                    type: 'warning'
                  })
                  if (confirmed) {
                    dispatch({ type: 'DELETE_THREAD', payload: { id: thread.id } } as any)
                  }
                }}
              >
                <Trash size={14} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                aria-label="Download thread"
                onClick={handleDownload}
              >
                <Download size={14} />
              </Button>
            </div>
          )}
          <div className="w-fit">
          {isEditing ? (
            <div className="space-y-2" onClick={(e) => { e.preventDefault(); e.stopPropagation() }}>
              <div className="flex items-center gap-2">
                <CopyButton value={title || ''} title={t('copy') || 'Copy'} />
                <Textarea
                  ref={threadTitleTextareaRef}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="min-h-[2rem] resize-none field-sizing-content flex-1"
                  onKeyDown={handleKey}
                  autoFocus
                />
                <Button size="sm" className="bg-success text-success-foreground hover:bg-success/90" onClick={(e) => { e.preventDefault(); e.stopPropagation(); save() }}>
                  <Check size={12} className="mr-1" />
                  {t('save')}
                </Button>
                <Button size="sm" className="bg-error text-error-foreground hover:bg-error/90" onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancel() }}>
                  <X size={12} className="mr-1" />
                  {t('cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <h3
                  className={cn(
                    "font-medium text-sm p-2 rounded w-fit",
                    !readOnly && "hover:bg-accent/20 cursor-pointer"
                  )}
                  onClick={onTitleClick}
                >
                  {thread.title || (
                    <span className="text-muted-foreground italic">{t('clickToAddText')}</span>
                  )}
                </h3>
                <CopyButton value={thread.title || ''} title={t('copy') || 'Copy'} />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost2"
                      size="sm"
                      className="h-auto w-auto p-1 text-pdf-export hover:text-pdf-export-hover"
                      onClick={handleExportPDF}
                      aria-label="Export to PDF"
                    >
                      <FilePdf size={24} weight="fill" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('exportPDF') || 'Export to PDF'}</TooltipContent>
                </Tooltip>
                <span className="text-xs text-muted-foreground">{t('updated')} {updatedDate}</span>
              </div>
            </>
          )}
          </div>
        </div>
        <div className="flex items-center ml-4">
          <div
            className="flex items-center gap-1 text-xs text-muted-foreground"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              id={`thread-${thread.id}-alternate`}
              checked={isAlternate}
              onCheckedChange={(checked) => onToggleAlternate(Boolean(checked))}
              disabled={readOnly}
              aria-label={alternateLabel}
            />
            <span>{alternateLabel}</span>
          </div>
          {verboseMode && openQuestionCount > 0 && (
            <div className="text-right">
              <div className="text-xs text-muted-foreground">{t('openQuestions')}</div>
              <Badge variant="secondary" className="text-xs">
                {openQuestionCount}
              </Badge>
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

// Compact Thread View Component for alternate mode
interface CompactThreadViewProps {
  thread: Thread
  bgClass: string
  openQuestionCount: number
  updatedDate: string
  readOnly: boolean
  qaNodes: Record<string, QANode>
  dispatch: React.Dispatch<any>
  onNavigate: () => void
  isAlternate: boolean
  onToggleAlternate: (value: boolean) => void
  registerSaveHandler: (fn: () => void) => void
  unregisterSaveHandler: (fn: () => void) => void
  registerCancelHandler: (fn: () => void) => void
  unregisterCancelHandler: (fn: () => void) => void
}

function CompactThreadView({ thread, bgClass, openQuestionCount, updatedDate, readOnly, qaNodes, dispatch, onNavigate, isAlternate, onToggleAlternate, registerSaveHandler, unregisterSaveHandler, registerCancelHandler, unregisterCancelHandler }: CompactThreadViewProps) {
  const { t } = useTranslation()
  const { state } = useApp()
  const dialog = useDialog()
  const verboseMode = state.settings?.verboseMode ?? true
  const [isEditing, setIsEditing] = useState(false)
  const [title, setTitle] = useState(thread.title)
  const alternateLabel = t('alternateMode')

  // Register global save/cancel handlers while editing thread title
  useEffect(() => {
    if (readOnly || !isEditing) return
    const saveCb = () => save()
    const cancelCb = () => cancel()
    registerSaveHandler(saveCb)
    registerCancelHandler(cancelCb)
    return () => {
      unregisterSaveHandler(saveCb)
      unregisterCancelHandler(cancelCb)
    }
  }, [isEditing, title, thread.title, readOnly])

  const onTitleClick: React.MouseEventHandler = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!readOnly) {
      setIsEditing(true)
    }
  }

  const handleNavigate = () => {
    if (isEditing) return
    onNavigate()
  }

  const save = () => {
    const next = title.trim()
    if (!next) return
    if (next !== thread.title) {
      dispatch({ type: 'UPDATE_THREAD', payload: { id: thread.id, title: next } } as any)
    }
    setIsEditing(false)
  }

  const cancel = () => {
    setTitle(thread.title)
    setIsEditing(false)
  }

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
      e.preventDefault()
      save()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  const handleExportPDF = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      const entity = state.entities[Object.keys(state.entities).find(id => 
        state.entities[id].threadIds.includes(thread.id)
      ) || '']
      
      await exportThreadToPDF({
        thread,
        qaNodes: state.qaNodes,
        entityName: entity?.name || 'Unknown'
      })
    } catch (error) {
      console.error('PDF export failed:', error)
      await dialog.error({ message: t('exportError') || 'Failed to export PDF' })
    }
  }

  const flatNodes = getFlatNodeList(thread.rootQuestionId, qaNodes)

  return (
  <Card className={cn("p-2 border-2 border-border")}> 
      {/* Thread Header */}
      <div
        className={cn(
          "mb-2 rounded-md -mx-2 px-2 pt-1 pb-0 transition-colors",
          !isEditing && "cursor-pointer hover:bg-accent/30"
        )}
        onClick={handleNavigate}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1">
            {!readOnly && (
              <Button
                variant="ghost-destructive"
                size="sm"
                aria-label="Delete thread"
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const confirmed = await dialog.confirm({
                    message: t('confirmDeleteThread', { title: thread.title }),
                    type: 'warning'
                  })
                  if (confirmed) {
                    dispatch({ type: 'DELETE_THREAD', payload: { id: thread.id } } as any)
                  }
                }}
              >
                <Trash size={14} />
              </Button>
            )}
         
            <CopyButton value={thread.title || ''} title={t('copy') || 'Copy'} />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost2"
                  size="sm"
                  className="h-auto w-auto p-1 text-pdf-export hover:text-pdf-export-hover"
                  onClick={handleExportPDF}
                  aria-label="Export to PDF"
                >
                  <FilePdf size={24} weight="fill" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('exportPDF') || 'Export to PDF'}</TooltipContent>
            </Tooltip>

            {isEditing ? (
              <>
                <Textarea
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="min-h-[2rem] resize-none field-sizing-content flex-1"
                  onKeyDown={handleKey}
                  autoFocus
                />                
                <Button size="sm" className="bg-success text-success-foreground hover:bg-success/90" onClick={(e) => { e.preventDefault(); e.stopPropagation(); save() }}>
                  <Check size={12} className="mr-1" />
                  {t('save')}
                </Button>
                <Button size="sm" className="bg-error text-error-foreground hover:bg-error/90" onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancel() }}>
                  <X size={12} className="mr-1" />
                  {t('cancel')}
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <h3
                  className={cn(
                    "font-medium text-sm p-2 rounded w-fit",
                    !readOnly && "hover:bg-accent/20 cursor-pointer"
                  )}
                  onClick={onTitleClick}
                >
                  {thread.title || (
                    <span className="text-muted-foreground italic">{t('clickToAddText')}</span>
                  )}
                </h3>
                <span className="text-xs text-muted-foreground">{t('updated')} {updatedDate}</span>
              </div>
            )}
        </div>
        <div className="flex items-center ml-4">
          <div
            className="flex items-center gap-1 text-xs text-muted-foreground"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              id={`compact-thread-${thread.id}-alternate`}
              checked={isAlternate}
              onCheckedChange={(checked) => onToggleAlternate(Boolean(checked))}
              disabled={readOnly}
              aria-label={alternateLabel}
            />
            <span>{alternateLabel}</span>
          </div>
          {verboseMode && openQuestionCount > 0 && (
            <div className="text-right">
              <div className="text-xs text-muted-foreground">{t('openQuestions')}</div>
              <Badge variant="secondary" className="text-xs">
                {openQuestionCount}
              </Badge>
            </div>
          )}
        </div>
        </div>
      </div>
      
      {/* Compact Q&A Content */}
      <div className="border-t pt-1">
        <div className="space-y-1.5">
          {(() => {
            const lastQuestion = [...flatNodes].reverse().find(n => n.node.type === 'question')?.node.id
            return flatNodes.map(({ node, level, parentId }) => (
            <CompactQAItem
              key={node.id}
              node={node}
              level={level}
              parentId={parentId}
              lastQuestionId={lastQuestion}
              dispatch={dispatch}
              readOnly={readOnly}
              registerSaveHandler={registerSaveHandler}
              unregisterSaveHandler={unregisterSaveHandler}
              registerCancelHandler={registerCancelHandler}
              unregisterCancelHandler={unregisterCancelHandler}
            />
            ))
          })()}
        </div>
      </div>
    </Card>
  )
}

// Helper function to get all nodes in flat order with metadata
function getFlatNodeList(rootQuestionId: string, qaNodes: Record<string, QANode>): Array<{ node: QANode, parentId?: string, type: 'question' | 'answer', level: number }> {
  const result: Array<{ node: QANode, parentId?: string, type: 'question' | 'answer', level: number }> = []
  
  function traverse(nodeId: string, parentId?: string, level: number = 0) {
    const node = qaNodes[nodeId]
    if (!node) return
    
    result.push({ node, parentId, type: node.type, level })
    
    if (node.type === 'question' && node.answerId) {
      traverse(node.answerId, nodeId, level)
    } else if (node.type === 'answer') {
      node.followUpQuestionIds.forEach(questionId => {
        traverse(questionId, nodeId, level + 1)
      })
    }
  }
  
  traverse(rootQuestionId)
  return result
}

interface CompactQAItemProps {
  node: QANode
  level: number
  parentId?: string
  lastQuestionId: string | undefined
  dispatch: React.Dispatch<any>
  readOnly?: boolean
  registerSaveHandler: (fn: () => void) => void
  unregisterSaveHandler: (fn: () => void) => void
  registerCancelHandler: (fn: () => void) => void
  unregisterCancelHandler: (fn: () => void) => void
}

function CompactQAItem({ node, level, parentId, lastQuestionId, dispatch, readOnly = false, registerSaveHandler, unregisterSaveHandler, registerCancelHandler, unregisterCancelHandler }: CompactQAItemProps) {
  const { t } = useTranslation()
  const { state } = useApp()
  const dialog = useDialog()
  const verboseMode = state.settings?.verboseMode ?? true

  const htmlToPlain = (source?: string) => {
    if (!source) return ''
    try {
      const doc = new DOMParser().parseFromString(source, 'text/html')
      return doc.body.textContent || ''
    } catch {
      return source
    }
  }

  const paddingLeft = level * 16
  const isQuestion = node.type === 'question'
  const isAnswer = node.type === 'answer'
  const answerNode = isAnswer ? node as Answer : undefined
  const consequences = answerNode?.consequences ?? []
  const [editingConsequenceId, setEditingConsequenceId] = useState<string | null>(null)
  const [consequenceDraft, setConsequenceDraft] = useState<Record<string, { text: string; completed: boolean }>>({})
  const consequenceTextareaRef = useRef<HTMLTextAreaElement>(null)
  const placeholder = isQuestion
    ? (t('enterQuestion') || 'Enter question...')
    : (t('enterAnswer') || 'Enter answer...')

  const [isEditing, setIsEditing] = useState(!readOnly && !htmlToPlain(node.text).trim().length)
  const [editHtml, setEditHtml] = useState(node.text || '')
  const [editText, setEditText] = useState(htmlToPlain(node.text))
  const [viewHtml, setViewHtml] = useState(node.text || '')

  useEffect(() => {
    const html = node.text || ''
    setEditHtml(html)
    setViewHtml(html)
    setEditText(htmlToPlain(html))
  }, [node.text])

  // Move cursor to end when editing a consequence
  useEffect(() => {
    if (editingConsequenceId && consequenceTextareaRef.current) {
      const textarea = consequenceTextareaRef.current
      const length = textarea.value.length
      textarea.setSelectionRange(length, length)
    }
  }, [editingConsequenceId])

  const plainView = htmlToPlain(viewHtml)

  const handleSave = () => {
    const payloadHtml = (editHtml || '').trim()
    const hasContent = (editText || '').trim().length > 0
    if (!hasContent) return

    if (isQuestion) {
      dispatch({ type: 'UPDATE_QUESTION', payload: { id: node.id, text: payloadHtml } })
    } else {
      dispatch({ type: 'UPDATE_ANSWER', payload: { id: node.id, text: payloadHtml } })
    }
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditHtml(node.text || '')
    setEditText(htmlToPlain(node.text))
    setViewHtml(node.text || '')
    setIsEditing(false)
  }

  const toggleConsequenceEditing = (consequenceId: string, startEditing: boolean, initialTitle: string, completed: boolean) => {
    if (readOnly) return
    if (startEditing) {
      setEditingConsequenceId(consequenceId)
      setConsequenceDraft((prev) => ({
        ...prev,
        [consequenceId]: {
          text: initialTitle,
          completed
        }
      }))
    } else {
      setEditingConsequenceId(null)
      setConsequenceDraft((prev) => {
        if (!(consequenceId in prev)) return prev
        const { [consequenceId]: _omit, ...rest } = prev
        return rest
      })
    }
  }

  const updateConsequenceDraft = (consequenceId: string, updates: Partial<{ text: string; completed: boolean }>) => {
    const original = consequences.find((c) => c.id === consequenceId)
    setConsequenceDraft((prev) => {
      const previous = prev[consequenceId]
      return {
        ...prev,
        [consequenceId]: {
          text: updates.text ?? previous?.text ?? original?.title ?? '',
          completed: updates.completed ?? previous?.completed ?? Boolean(original?.completedAt)
        }
      }
    })
  }

  const handleConsequenceSave = (consequenceId: string) => {
    const draft = consequenceDraft[consequenceId]
    const original = consequences.find((c) => c.id === consequenceId)
    if (!draft) {
      setEditingConsequenceId(null)
      return
    }

    const nextText = (draft.text ?? '').trim()
    const nextCompleted = !!draft.completed
    const originalCompleted = Boolean(original?.completedAt)
    const payload: Record<string, any> = {}

    if (nextText && nextText !== (original?.title ?? '')) {
      payload.title = nextText
    }
    if (nextCompleted !== originalCompleted) {
      payload.completed = nextCompleted
    }

    if (Object.keys(payload).length > 0) {
      dispatch({
        type: 'UPDATE_CONSEQUENCE',
        payload: {
          answerId: node.id,
          consequenceId,
          ...payload
        }
      } as any)
    }

    setEditingConsequenceId(null)
    setConsequenceDraft((prev) => {
      if (!(consequenceId in prev)) return prev
      const { [consequenceId]: _omit, ...rest } = prev
      return rest
    })
  }

  const handleConsequenceCancel = (consequenceId: string) => {
    setEditingConsequenceId(null)
    setConsequenceDraft((prev) => {
      if (!(consequenceId in prev)) return prev
      const { [consequenceId]: _omit, ...rest } = prev
      return rest
    })
  }

  const computeConsequenceWidth = (value: string) => {
    const lines = (value || '').split('\n')
    const longest = Math.max(16, ...lines.map((line) => line.trim().length + 1))
    return Math.min(longest, 72)
  }

  // Register global save/cancel handlers while this node is in edit mode
  useEffect(() => {
    if (readOnly) return
    if (!isEditing) return
    const saveCb = () => handleSave()
    const cancelCb = () => handleCancel()
    registerSaveHandler(saveCb)
    registerCancelHandler(cancelCb)
    return () => {
      unregisterSaveHandler(saveCb)
      unregisterCancelHandler(cancelCb)
    }
  }, [isEditing, editHtml, editText, readOnly])

  // Register while a consequence is being edited
  useEffect(() => {
    if (readOnly) return
    if (!editingConsequenceId) return
    const saveCb = () => handleConsequenceSave(editingConsequenceId)
    const cancelCb = () => handleConsequenceCancel(editingConsequenceId)
    registerSaveHandler(saveCb)
    registerCancelHandler(cancelCb)
    return () => {
      unregisterSaveHandler(saveCb)
      unregisterCancelHandler(cancelCb)
    }
  }, [editingConsequenceId, consequenceDraft, readOnly])

  // Creation helpers (mirroring ThreadPage rules)
  const createEmptyAnswer = () => {
    if (readOnly) return
    if (node.type === 'question' && !node.answerId) {
      dispatch({ type: 'CREATE_ANSWER', payload: { questionId: node.id, text: '', status: 'inconclusive' } })
    }
  }

  const createEmptyFollowUpQuestion = () => {
    if (readOnly) return
    if (node.type === 'answer' && parentId && parentId === lastQuestionId) {
      dispatch({ type: 'CREATE_QUESTION', payload: { text: '', parentAnswerId: node.id } })
    }
  }

  const handleAddConsequence = () => {
    if (readOnly) return
    if (node.type === 'answer') {
      const newConsequenceId = `consequence-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      dispatch({ type: 'ADD_CONSEQUENCE', payload: { answerId: node.id, title: '', consequenceId: newConsequenceId } })
      // Set the newly created consequence as editing
      setEditingConsequenceId(newConsequenceId)
      setConsequenceDraft((prev) => ({
        ...prev,
        [newConsequenceId]: {
          text: '',
          completed: false
        }
      }))
    }
  }

  const handleStatusToggle = (checked: boolean) => {
    if (readOnly) return
    if (node.type === 'answer') {
      dispatch({ type: 'UPDATE_ANSWER', payload: { id: node.id, status: checked ? 'conclusive' : 'inconclusive' } })
    }
  }

  const handleDeleteNode = async () => {
    if (readOnly) return
    if (node.type === 'question') {
      const confirmed = await dialog.confirm({
        message: t('confirmDeleteQuestion') || 'Delete this question and its subtree?',
        type: 'warning'
      })
      if (confirmed) {
        dispatch({ type: 'DELETE_QUESTION', payload: { id: node.id } } as any)
      }
    } else if (node.type === 'answer') {
      const confirmed = await dialog.confirm({
        message: t('confirmDeleteAnswer') || 'Delete this answer and its subtree?',
        type: 'warning'
      })
      if (confirmed) {
        dispatch({ type: 'DELETE_ANSWER', payload: { id: node.id } } as any)
      }
    }
  }

  return (
    <div
      className={cn('p-1.5 rounded', {
      'bg-info-soft': isQuestion,
    'bg-success-soft': isAnswer && (node as Answer).status === 'conclusive',
    'bg-error-soft': isAnswer && (node as Answer).status === 'inconclusive'
      })}
      style={{ paddingLeft: `${paddingLeft + 8}px` }}
    >
      <div className="flex items-center gap-2">
        {verboseMode && (
        <Badge
          variant="secondary"
          className={cn(
            'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold uppercase border',
            isQuestion && 'bg-info text-info-foreground border-info-border',
            isAnswer && (node as Answer).status === 'conclusive' && 'bg-success text-success-foreground border-success-border',
            isAnswer && (node as Answer).status === 'inconclusive' && 'bg-error text-error-foreground border-error-border'
          )}
        >
          {isQuestion ? 'Q' : 'A'}
          {isAnswer && (
            <span className="ml-1">
              {(node as Answer).status === 'conclusive' ? '✓' : '✗'}
            </span>
          )}
        </Badge>
        )}

        <div className="flex-1 min-w-0">
          {isEditing && !readOnly ? (
            <div className="space-y-2" onKeyDown={(e)=>{ if(e.key==='Escape'){ e.preventDefault(); handleCancel() }}}>
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost-destructive" size="sm" className="h-6 w-6 p-0 text-destructive hover:text-destructive" onClick={(e)=>{e.stopPropagation();handleDeleteNode()}}>
                      <Trash className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="px-2 py-1 text-xs">{t('delete')}</TooltipContent>
                </Tooltip>
                <CopyButton value={editText || ''} valueHtml={editHtml || ''} title={t('copy') || 'Copy'} />
                {!readOnly && (
                  <div className="flex flex-col   gap-2">
                  <div className="flex items-center gap-1 shrink-0 mt-0.5" onClick={(e)=>e.stopPropagation()}>
                    {isQuestion && !node.answerId && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={(e)=>{e.stopPropagation();createEmptyAnswer()}}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="px-2 py-1 text-xs">{t('addAnswer')}</TooltipContent>
                      </Tooltip>
                    )}
                    {isAnswer && parentId && parentId === lastQuestionId && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={(e)=>{e.stopPropagation();createEmptyFollowUpQuestion()}}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="px-2 py-1 text-xs">{t('addFollowupQuestion')}</TooltipContent>
                      </Tooltip>
                    )}
                    {isAnswer && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center justify-center h-6 w-6">
                            <Checkbox id={`compact-conclusive-${node.id}`} checked={(node as Answer).status === 'conclusive'} onCheckedChange={(c)=>handleStatusToggle(!!c)} />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="px-2 py-1 text-xs">{t('conclusive')}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="flex justify-between gap-1.5 mt-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="default" 
                          className="h-6 px-2 text-[11px] bg-success text-success-foreground hover:bg-success/90"  
                          onClick={handleSave}>
                          <Check className="h-8 w-8" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="px-2 py-1 text-xs">{t('save')}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="default" 
                          className="h-6 px-2 text-[11px] bg-error text-error-foreground hover:bg-error/90" 
                          onClick={handleCancel}>
                          <X className="h-8 w-8" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="px-2 py-1 text-xs">{t('cancel')}</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
                )}
                <div className="flex-1">
                  <LexicalRichTextEditor
                    initialValue={editHtml}
                    onChange={(html,plain)=>{ setEditHtml(html); setEditText(plain); setViewHtml(html) }}
                    placeholder={placeholder}
                    autoFocus
                    className="min-h-[2.75rem]"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className={cn('text-xs p-1 rounded min-h-[1.75rem]', !readOnly && 'cursor-pointer hover:bg-card/50')} onClick={()=>{ if(!readOnly) setIsEditing(true) }}>
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive hover:text-destructive" onClick={handleDeleteNode}>
                      <Trash className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="px-2 py-1 text-xs">{t('delete')}</TooltipContent>
                </Tooltip>
                <CopyButton value={plainView} valueHtml={viewHtml || ''} title={t('copy') || 'Copy'} />
                {!readOnly && (
                  <div className="flex items-center gap-1 shrink-0 mt-0.5" onClick={(e)=>e.stopPropagation()}>
                    {isQuestion && !node.answerId && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={createEmptyAnswer}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="px-2 py-1 text-xs">{t('addAnswer')}</TooltipContent>
                      </Tooltip>
                    )}
                    {isAnswer && parentId && parentId === lastQuestionId && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={createEmptyFollowUpQuestion}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="px-2 py-1 text-xs">{t('addFollowupQuestion')}</TooltipContent>
                      </Tooltip>
                    )}
                    {isAnswer && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center justify-center h-6 w-6">
                            <Checkbox id={`compact-conclusive-${node.id}`} checked={(node as Answer).status === 'conclusive'} onCheckedChange={(c)=>handleStatusToggle(!!c)} />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="px-2 py-1 text-xs">{t('conclusive')}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                )}
                <div className="flex-1">
                  {(() => { const hasContent = (plainView || '').trim().length > 0; if (!hasContent) { return <span className="text-muted-foreground italic">{t('clickToAddText')}</span> } return <div dangerouslySetInnerHTML={{ __html: viewHtml || '' }} /> })()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isAnswer && (
        <div className="mt-1.5 ml-6 flex items-start gap-2">
          {verboseMode && (
          <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('consequences')}
            {!readOnly && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={handleAddConsequence}>
                    <Plus className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="px-2 py-1 text-xs">{t('addConsequence')}</TooltipContent>
              </Tooltip>
            )}
          </div>
          )}
          {!verboseMode && !readOnly && (
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={handleAddConsequence}>
                    <Plus className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="px-2 py-1 text-xs">{t('addConsequence')}</TooltipContent>
              </Tooltip>
            </div>
          )}
          <div className="flex-1 space-y-1.5">
          {consequences.length > 0 && consequences.map((consequence) => {
            const consequenceId = consequence.id
            const draft = consequenceDraft[consequenceId]
            const isCompleted = draft?.completed ?? Boolean(consequence.completedAt)
            const isEditingConsequence = editingConsequenceId === consequenceId
            const titleDraft = draft?.text ?? consequence.title ?? ''
            const plainDraft = titleDraft
            const widthCh = computeConsequenceWidth(titleDraft)

            return (
              <div
                key={consequenceId}
                className={cn(
                  'rounded border px-2 py-1.5 text-xs transition-colors',
                  isCompleted ? 'bg-consequence-completed border-consequence-completed-border' : 'bg-consequence-pending border-consequence-pending-border'
                )}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  {!readOnly && ((
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          title={t('delete') || 'Delete'}
                          onClick={async () => {
                            const confirmed = await dialog.confirm({
                              message: t('confirmDeleteConsequence') || 'Rimuovere questa conseguenza?',
                              type: 'warning'
                            })
                            if (confirmed) {
                              dispatch({
                                type: 'DELETE_CONSEQUENCE',
                                payload: { answerId: node.id, consequenceId }
                              } as any)
                            }
                          }}
                        >
                          <Trash size={12} />
                        </Button>
                      )
                    )}

                  <CopyButton value={plainDraft} title={t('copy') || 'Copy'} />
                  <Checkbox
                    checked={isCompleted}
                    disabled={readOnly}
                    onCheckedChange={(checked) => {
                      if (readOnly) return
                      const nextCompleted = Boolean(checked)
                      updateConsequenceDraft(consequenceId, { completed: nextCompleted })
                      dispatch({
                        type: 'UPDATE_CONSEQUENCE',
                        payload: { answerId: node.id, consequenceId, completed: nextCompleted }
                      } as any)
                    }}
                    className="mt-0.5"
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isEditingConsequence && !readOnly ? (
                      <Textarea
                        ref={consequenceTextareaRef}
                        value={titleDraft}
                        onChange={(e) => updateConsequenceDraft(consequenceId, { text: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                            e.preventDefault()
                            handleConsequenceSave(consequenceId)
                          } else if (e.key === 'Escape') {
                            e.preventDefault()
                            handleConsequenceCancel(consequenceId)
                          }
                        }}
                        className="min-h-[2rem] resize-none text-xs field-sizing-content"
                        style={{ width: `${widthCh}ch` }}
                        autoFocus
                      />
                    ) : (
                      <div
                        className={cn(
                          'rounded px-1 py-0.5 whitespace-pre-wrap',
                          !readOnly && 'cursor-pointer hover:bg-card/60',
                          isCompleted && 'line-through opacity-70'
                        )}
                        style={{ width: `${widthCh}ch` }}
                        onClick={() => toggleConsequenceEditing(consequenceId, true, consequence.title || '', isCompleted)}
                      >
                        {plainDraft ? plainDraft : (
                          <span className="text-muted-foreground italic">{t('clickToAddText')}</span>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground shrink-0">
                      {!readOnly && isEditingConsequence && (
                          <>
                            <Button
                              size="sm"
                              className="h-6 px-2 text-[11px] bg-success text-success-foreground hover:bg-success/90"
                              onClick={() => handleConsequenceSave(consequenceId)}
                              title={t('save') || 'Save'}
                            >
                              <Check size={11} />
                            </Button>
                            <Button
                              size="sm"
                              className="h-6 px-2 text-[11px] bg-error text-error-foreground hover:bg-error/90"
                              onClick={() => handleConsequenceCancel(consequenceId)}
                              title={t('cancel') || 'Cancel'}
                            >
                              <X size={11} />
                            </Button>
                          </>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        </div>
      )}
    </div>
  )
}